// src/pages/pedidos/DashPedidos.js
import React, { useEffect, useMemo, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  FaClipboardList,
  FaClock,
  FaMoneyBillWave,
  FaBan,
  FaChartLine,
  FaTruck,
} from "react-icons/fa";
import { Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";

import "../../components/leiaute/Content.css";
import { fetchData } from "../../services/Api";
import { formatCurrency } from "../../utils/numberUtils";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const chartColors = ["#007bff", "#28a745", "#ffc107", "#dc3545", "#6c757d", "#17a2b8", "#fd7e14"];

function DashPedidos() {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const formatDate = (date) => date.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(formatDate(firstDayOfMonth));
  const [endDate, setEndDate] = useState(formatDate(today));
  const [dash, setDash] = useState({
    cards: {
      pedidosPeriodo: 0,
      pedidosAbertos: 0,
      pedidosCancelados: 0,
      valorTotal: 0,
      ticketMedio: 0,
      tempoMedioPreparoMin: 0,
      percentualCancelamento: 0,
    },
    pedidosPorHorario: [],
    pedidosPorDiaSemana: [],
    pedidosPorOrigem: [],
    pedidosPorStatus: [],
    pedidosPorTipoEntrega: [],
  });

  useEffect(() => {
    const fetchDashPedidos = async () => {
      try {
        const params = {
          DataIni: startDate,
          DataFim: `${endDate} 23:59:59`,
        };

        const data = await fetchData("/DashPedido", params);
        setDash({
          cards: data.cards || {},
          pedidosPorHorario: data.pedidosPorHorario || [],
          pedidosPorDiaSemana: data.pedidosPorDiaSemana || [],
          pedidosPorOrigem: data.pedidosPorOrigem || [],
          pedidosPorStatus: data.pedidosPorStatus || [],
          pedidosPorTipoEntrega: data.pedidosPorTipoEntrega || [],
        });
      } catch (error) {
        console.error("Erro ao carregar o dashboard de pedidos:", error);
      }
    };

    fetchDashPedidos();
  }, [startDate, endDate]);

  const linePedidosHorario = useMemo(() => ({
    labels: dash.pedidosPorHorario.map((item) => item.label),
    datasets: [
      {
        label: "Pedidos por horário",
        data: dash.pedidosPorHorario.map((item) => item.qtde),
        borderColor: "#007bff",
        backgroundColor: "rgba(0, 123, 255, 0.2)",
        tension: 0.4,
      },
    ],
  }), [dash.pedidosPorHorario]);

  const linePedidosDiaSemana = useMemo(() => ({
    labels: dash.pedidosPorDiaSemana.map((item) => item.label),
    datasets: [
      {
        label: "Pedidos por dia da semana",
        data: dash.pedidosPorDiaSemana.map((item) => item.qtde),
        borderColor: "#28a745",
        backgroundColor: "rgba(40, 167, 69, 0.2)",
        tension: 0.35,
      },
    ],
  }), [dash.pedidosPorDiaSemana]);

  const pieOrigem = useMemo(() => ({
    labels: dash.pedidosPorOrigem.map((item) => item.descricao),
    datasets: [
      {
        data: dash.pedidosPorOrigem.map((item) => item.qtde),
        backgroundColor: chartColors,
      },
    ],
  }), [dash.pedidosPorOrigem]);

  const pieStatus = useMemo(() => ({
    labels: dash.pedidosPorStatus.map((item) => item.descricao),
    datasets: [
      {
        data: dash.pedidosPorStatus.map((item) => item.qtde),
        backgroundColor: chartColors,
      },
    ],
  }), [dash.pedidosPorStatus]);

  const pieTipoEntrega = useMemo(() => ({
    labels: dash.pedidosPorTipoEntrega.map((item) => item.descricao),
    datasets: [
      {
        data: dash.pedidosPorTipoEntrega.map((item) => item.qtde),
        backgroundColor: chartColors,
      },
    ],
  }), [dash.pedidosPorTipoEntrega]);

  const cards = dash.cards || {};

  return (
    <div className="p-4 dashboard-scroll">
      <div className="d-flex justify-content-between align-items-center mb-3" style={{ marginTop: "-20px" }}>
        <h2>Dashboard de Pedidos</h2>

        <div className="d-flex">
          <div className="me-2" style={{ maxWidth: "130px" }}>
            <label htmlFor="dashPedidosStartDate" className="form-label">Data Inicial:</label>
            <input
              type="date"
              id="dashPedidosStartDate"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div style={{ maxWidth: "130px" }}>
            <label htmlFor="dashPedidosEndDate" className="form-label">Data Final:</label>
            <input
              type="date"
              id="dashPedidosEndDate"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-md-3">
          <div className="card text-center shadow-sm" style={{ backgroundColor: "#f8f9fa", border: "none" }}>
            <div className="card-body">
              <FaClipboardList size={40} color="#007bff" className="mb-3" />
              <h5 className="card-title text-primary">Pedidos no Período</h5>
              <p className="card-text fs-2 fw-bold text-dark">{cards.pedidosPeriodo || 0}</p>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card text-center shadow-sm" style={{ backgroundColor: "#e2f0d9", border: "none" }}>
            <div className="card-body">
              <FaMoneyBillWave size={40} color="#28a745" className="mb-3" />
              <h5 className="card-title text-success">Ticket Médio</h5>
              <p className="card-text fs-2 fw-bold text-dark">{formatCurrency(cards.ticketMedio || 0)}</p>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card text-center shadow-sm" style={{ backgroundColor: "#fff3cd", border: "none" }}>
            <div className="card-body">
              <FaClock size={40} color="#ffc107" className="mb-3" />
              <h5 className="card-title text-warning">Tempo Médio Preparo</h5>
              <p className="card-text fs-2 fw-bold text-dark">{Number(cards.tempoMedioPreparoMin || 0).toFixed(0)} min</p>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card text-center shadow-sm" style={{ backgroundColor: "#ffecec", border: "none" }}>
            <div className="card-body">
              <FaBan size={40} color="#dc3545" className="mb-3" />
              <h5 className="card-title text-danger">Cancelamento</h5>
              <p className="card-text fs-2 fw-bold text-dark">{Number(cards.percentualCancelamento || 0).toFixed(2)}%</p>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mt-1">
        <div className="col-md-4">
          <div className="card text-center shadow-sm" style={{ backgroundColor: "#eef5ff", border: "none" }}>
            <div className="card-body">
              <FaClock size={36} color="#0d6efd" className="mb-2" />
              <h6 className="card-title text-primary mb-1">Pedidos Abertos</h6>
              <div className="fs-2 fw-bold text-dark">{cards.pedidosAbertos || 0}</div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card text-center shadow-sm" style={{ backgroundColor: "#f8f9fa", border: "none" }}>
            <div className="card-body">
              <FaChartLine size={36} color="#6c757d" className="mb-2" />
              <h6 className="card-title mb-1">Valor Total</h6>
              <div className="fs-2 fw-bold text-dark">{formatCurrency(cards.valorTotal || 0)}</div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card text-center shadow-sm" style={{ backgroundColor: "#fff7e6", border: "none" }}>
            <div className="card-body">
              <FaBan size={36} color="#fd7e14" className="mb-2" />
              <h6 className="card-title mb-1" style={{ color: "#fd7e14" }}>Pedidos Cancelados</h6>
              <div className="fs-2 fw-bold text-dark">{cards.pedidosCancelados || 0}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="row mt-5">
        <div className="col-md-6">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title">Pedidos por Horário</h5>
              <Line data={linePedidosHorario} />
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title">Origem dos Pedidos</h5>
              <Pie data={pieOrigem} />
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title">Pedidos por Status</h5>
              <Pie data={pieStatus} />
            </div>
          </div>
        </div>
      </div>

      <div className="row mt-5">
        <div className="col-md-6">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title">Pedidos por Dia da Semana</h5>
              <Line data={linePedidosDiaSemana} />
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <h5 className="card-title mb-0">Tipo de Entrega</h5>
                <FaTruck title="Tipo de entrega" />
              </div>
              <div className="mt-2">
                <Pie data={pieTipoEntrega} />
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title">Resumo por Origem</h5>
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Origem</th>
                      <th className="text-end">Qtde</th>
                      <th className="text-end">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dash.pedidosPorOrigem.map((item) => (
                      <tr key={item.origemPedido}>
                        <td>{item.descricao}</td>
                        <td className="text-end">{item.qtde}</td>
                        <td className="text-end">{formatCurrency(item.valorTotal)}</td>
                      </tr>
                    ))}
                    {dash.pedidosPorOrigem.length === 0 && (
                      <tr>
                        <td colSpan="3" className="text-center">Nenhum dado disponível.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashPedidos;
